import PropTypes from 'prop-types';
import React from 'react';

import t from '../../../labels/labelling-utils';
import { ARROW_POSITION_RIGHT } from '../../../typings/design-tokens';
import { IClassNames, makeClassNames } from '../../../utils/styles';
import Cta from '../../atoms/cta/cta';
import ErrorComponent from '../../atoms/error/error';
import ResultsTitle from '../../atoms/results-title/results-title';
import Spacer from '../../atoms/spacer/spacer';
import Pager, { IPager } from '../../molecules/pager/pager';
import TopScorersTable, { ITopScorersRow, ITopScorersTable } from '../../organisms/top-scorers-table/top-scorers-table';
import ErrorBoundary from '../error-boundary/error-boundary';

import './top-scorers.scss';

export interface ITopScorers extends ITopScorersTable {
  isTopTen?: boolean;
  pages?: IPager;
  ctaLink?: string;
  showTopScorersTitle?: boolean;
}

const classNames = (): IClassNames =>
  makeClassNames({
    topScorers: 'top-scorers',
    cta: 'top-scorers__cta',
    pager: 'top-scorers__pager',
  });

const cn = classNames();

const getErrorComponent = () => <ErrorComponent>{t('unavailable.data')}</ErrorComponent>;

const isValidList = (rows: Array<ITopScorersRow>) => Boolean(rows && rows.length);

const getTopScorersFooter = (isTopTen: boolean, ctaLink: string | undefined, pages: IPager | undefined) => {
  const ctaText = t('competition.top.scorers.cta.view.all');

  if (isTopTen && ctaLink) {
    return (
      <div className={cn.cta}>
        <Cta iconPosition={ARROW_POSITION_RIGHT} href={ctaLink} mobileButton>
          {ctaText}
        </Cta>
      </div>
    );
  } else if (pages?.links.length) {
    return (
      <div className={cn.pager}>
        <Pager {...pages} />
      </div>
    );
  } else {
    return null;
  }
};

const TopScorers: React.FC<ITopScorers> = ({
  /* istanbul ignore next: default value */ rows = [],
  /* istanbul ignore next: default value */ className = 'top-scorers',
  /* istanbul ignore next: default value */ showTitles = true,
  /* istanbul ignore next: default value */ isTopTen = false,
  /* istanbul ignore next: default value */ showTopScorersTitle = false,
  pages,
  ctaLink,
}) => {
  return (
    <div className={cn.topScorers}>
      <React.Fragment>
        {showTopScorersTitle && (
          <React.Fragment>
            <ResultsTitle title={t('competition.top.scorers.title')} titleFontSize="14--16" />
            <Spacer size={20} />
          </React.Fragment>
        )}
        <ErrorBoundary hasError={!isValidList(rows)} errorComponent={getErrorComponent()}>
          <TopScorersTable rows={rows} className={className} showTitles={showTitles} />
          {getTopScorersFooter(isTopTen, ctaLink, pages)}
        </ErrorBoundary>
      </React.Fragment>
    </div>
  );
};

/* istanbul ignore else */ if (process.env.NODE_ENV !== 'production') {
  TopScorers.propTypes = {
    className: PropTypes.string,
    ctaLink: PropTypes.string,
    isTopTen: PropTypes.bool,
    // @ts-ignore well know problem with shapes
    pages: PropTypes.shape({
      links: PropTypes.array,
      onPageNumberChanged: PropTypes.func,
      pageNumber: PropTypes.number,
    }),
    rows: PropTypes.arrayOf(
      PropTypes.shape({
        areaIn: PropTypes.number.isRequired,
        areaOut: PropTypes.number.isRequired,
        goal: PropTypes.number.isRequired,
        head: PropTypes.number.isRequired,
        logoUrl: PropTypes.string.isRequired,
        penalty: PropTypes.number.isRequired,
        playerName: PropTypes.string.isRequired,
        position: PropTypes.number.isRequired,
      }).isRequired
    ).isRequired,
    showTitles: PropTypes.bool,
    showTopScorersTitle: PropTypes.bool,
  };
}

export default TopScorers;
